// ---------------------------------------------------------------------------
// Copy + predicate for the two Figma doors on the Sources page
// (`/studio/import`), measured 2026-08-25.
//
// Door 1 — the `.fig` card had no visible exit. A successful import is carried
// to `/ide/source-workspace` by the MCP handler
// (`import_openpencil_figma_source` -> `activateOpenPencilSourceWorkspace()`),
// but NOTHING in the dashboard links to that route, so a creator who imported
// earlier — or who reloaded, or who navigated back — had no way to reach the
// canvas, the tokens view, or the components view again. All three mount only
// from `source-surface.tsx`, which mounts only inside the IDE shell.
//
// Door 2 — the `figma_frame` card was offered as if it worked with no project
// open. Its handler refuses that case outright:
//
//   src/mcp/register-cogo-xyz-tools.ts (import_figma_frame)
//     const currentProjectId = useStudioStore.getState().currentProjectId;
//     if (!currentProjectId) { throw new Error(
//       "import_figma_frame requires an open Studio project ...") }
//
// `figmaFrameNeedsOpenProject` is that same condition, read from the same
// store field, so the card can say WHY before the creator pastes a URL and
// hands over a Figma token for a call that cannot land. The gate that keeps
// the two in step is derived from the handler's own source in
// `__tests__/import-page-source-doors.test.tsx` — if the handler stops keying
// its refusal on `currentProjectId`, that test fails rather than this file
// quietly drifting into a lie.
//
// Door 1 vs Door 2 — measured 2026-08-27. The two doors do not merely differ
// in convenience; they hand back different KINDS of thing, and nothing on
// screen said so:
//
//   * `.fig` upload  -> the real frames. `openpencil-source-canvas.tsx`
//     renders `<img src={source}>` per frame on a pan/zoom board.
//
//   * Figma URL      -> no artwork at all. The import calls exactly ONE Figma
//     endpoint, `https://api.figma.com/v1/files/{key}/nodes` — the layer TREE
//     — and turns it into `blocks["figma-frame"].ui.root`
//     (`cogo-xyz-figma-import.ts`). `imageRef` paints are captured into the
//     paint record but never resolved; the `/v1/images` endpoint that would
//     turn one into a URL is marked "not yet wired" and no code builds it.
//
// So a creator pasting a URL is shown STUDIO'S REBUILD of their design, and
// without disclosure reads every difference as Studio getting their design
// wrong rather than as a conversion. `frameProducesChip` / `fileProducesChip`
// separate the doors before the choice, `frameConversionNotice` states the
// mechanism before the run, and `frameImportedStatus` names the result after
// it — because the creator compares against Figma at THAT moment.
//
// The wording says what Studio DOES ("reads the layers and rebuilds"), never
// "you will never see an image". That distinction is load-bearing: four
// hardcoded demo file keys (`STATIC_FIGMA_TEMPLATE_BY_FILE_KEY`) resolve to
// PNGs shipped in `public/figma-visual-lock/`, so an absolute claim would be
// false there. Those rasters are Studio's own assets, never the creator's
// artwork fetched from Figma, so the mechanism sentence stays true for both.
//
// `__tests__/import-page-figma-conversion-disclosure.test.tsx` derives the
// claim from the import module's own source: the set of host-qualified Figma
// endpoints it builds must stay `{files}`. Wire `/v1/images` and that test
// goes red, so this copy cannot drift into a lie in either direction.
//
// The card is NOT disabled: a missing project is a step the creator can take
// (open or create one), unlike an appliance build where the network to Figma
// is closed by construction. Disabling would read as "this product cannot do
// frames", which is false.
// ---------------------------------------------------------------------------

import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface SourceDoorsCopy {
  /** Button that carries the creator to `/ide/source-workspace`. */
  openCanvas: string;
  /** Sub-line when this browser already holds an imported design. */
  openCanvasWithSource: string;
  /** Sub-line when nothing is imported yet — the canvas has its own picker. */
  openCanvasEmpty: string;
  /** Short chip on the `figma_frame` card. */
  frameNeedsProjectChip: string;
  /** Full disclosure shown on the frame panel, before any work is done. */
  frameNeedsProjectNotice: string;
  /** Status line when the creator runs the import with no project open. */
  frameNeedsProjectStatus: string;
  /** Chip on the `figma_frame` card: what a pasted URL hands back. */
  frameProducesChip: string;
  /** Chip on the `figma_file` card: what a `.fig` upload hands back. */
  fileProducesChip: string;
  /** Mechanism, disclosed on the frame panel before anything is sent. */
  frameConversionNotice: string;
  /**
   * The way out — look at the original frames instead. Held SEPARATE from the
   * notice because it names the `.fig` card, and that card is disabled
   * wherever `VITE_OPENPENCIL_IMPORT_API_URL` ships empty (the cloud build).
   * Offering it there would send a creator to a door they cannot open, on the
   * very panel whose job is to stop misleading them.
   */
  frameConversionAlternative: string;
  /** Status line naming the result, at the moment it can be compared. */
  frameImportedStatus: string;
}

const COPY: Record<LocaleCode, SourceDoorsCopy> = {
  en: {
    openCanvas: "Open the design canvas",
    openCanvasWithSource: "The design you imported is open there.",
    openCanvasEmpty:
      "The design canvas is where an imported .fig is read. You can pick a file there too.",
    frameNeedsProjectChip: "Needs an open project",
    frameNeedsProjectNotice:
      "A Figma frame is saved into a project's design records, so a project has to be open before one can be read. Nothing is sent to Figma until then.",
    frameNeedsProjectStatus:
      "Open a project first — a Figma frame is saved into that project's design records.",
    frameProducesChip: "Rebuilt with Cogo components",
    fileProducesChip: "Your frames, as you drew them",
    frameConversionNotice:
      "Studio reads this frame's layers from Figma and rebuilds it with Cogo components. Your artwork itself is not brought across, so what you get is Studio's rebuild — anything that looks different from your Figma file is the conversion, not your design.",
    frameConversionAlternative:
      "To look at the original frames, upload the .fig file instead.",
    frameImportedStatus:
      "Saved to Library as Studio's rebuild of the frame — compare it with your Figma file before building on it.",
  },
  ko: {
    openCanvas: "디자인 캔버스 열기",
    openCanvasWithSource: "가져온 디자인이 그곳에 열려 있습니다.",
    openCanvasEmpty:
      "가져온 .fig는 디자인 캔버스에서 읽습니다. 그곳에서 파일을 고를 수도 있습니다.",
    frameNeedsProjectChip: "열린 프로젝트가 필요합니다",
    frameNeedsProjectNotice:
      "Figma 프레임은 프로젝트의 디자인 기록에 저장되므로, 읽기 전에 프로젝트가 열려 있어야 합니다. 그때까지 Figma로는 아무것도 보내지 않습니다.",
    frameNeedsProjectStatus:
      "프로젝트를 먼저 여세요 — Figma 프레임은 그 프로젝트의 디자인 기록에 저장됩니다.",
    frameProducesChip: "Cogo 컴포넌트로 재구성",
    fileProducesChip: "그린 그대로의 프레임",
    frameConversionNotice:
      "Studio는 이 프레임의 레이어를 Figma에서 읽어 Cogo 컴포넌트로 다시 구성합니다. 원본 아트워크 자체는 가져오지 않으므로, 받게 되는 것은 Studio의 재구성본입니다 — Figma 파일과 다르게 보이는 부분은 디자인이 아니라 변환 때문입니다.",
    frameConversionAlternative:
      "원본 프레임을 보려면 .fig 파일을 올리세요.",
    frameImportedStatus:
      "프레임의 Studio 재구성본으로 라이브러리에 저장됐습니다 — 작업을 이어가기 전에 Figma 파일과 비교해 보세요.",
  },
  ja: {
    openCanvas: "デザインキャンバスを開く",
    openCanvasWithSource: "読み込んだデザインはそこで開いています。",
    openCanvasEmpty:
      "読み込んだ .fig はデザインキャンバスで読みます。そこでファイルを選ぶこともできます。",
    frameNeedsProjectChip: "開いているプロジェクトが必要です",
    frameNeedsProjectNotice:
      "Figma フレームはプロジェクトのデザイン記録に保存されるため、読み込む前にプロジェクトを開いておく必要があります。それまで Figma へは何も送信しません。",
    frameNeedsProjectStatus:
      "先にプロジェクトを開いてください — Figma フレームはそのプロジェクトのデザイン記録に保存されます。",
    frameProducesChip: "Cogo コンポーネントで再構成",
    fileProducesChip: "描いたままのフレーム",
    frameConversionNotice:
      "Studio はこのフレームのレイヤーを Figma から読み取り、Cogo コンポーネントで再構成します。アートワーク自体は持ち越されないため、得られるのは Studio の再構成結果です — Figma ファイルと違って見える箇所は、デザインではなく変換のせいです。",
    frameConversionAlternative:
      "元のフレームを見るには .fig ファイルをアップロードしてください。",
    frameImportedStatus:
      "フレームの Studio 再構成結果としてライブラリに保存しました — 作業を進める前に Figma ファイルと見比べてください。",
  },
  zh: {
    openCanvas: "打开设计画布",
    openCanvasWithSource: "你导入的设计已在那里打开。",
    openCanvasEmpty: "导入的 .fig 在设计画布中读取，你也可以在那里选择文件。",
    frameNeedsProjectChip: "需要打开一个项目",
    frameNeedsProjectNotice:
      "Figma 画框会保存到项目的设计记录中，因此读取前需要先打开一个项目。在此之前不会向 Figma 发送任何内容。",
    frameNeedsProjectStatus:
      "请先打开一个项目 —— Figma 画框会保存到该项目的设计记录中。",
    frameProducesChip: "用 Cogo 组件重建",
    fileProducesChip: "你画的原画框",
    frameConversionNotice:
      "Studio 会从 Figma 读取这个画框的图层，并用 Cogo 组件重建它。原始美术稿本身不会被带过来，所以你得到的是 Studio 的重建结果 — 与 Figma 文件看起来不同的地方，是转换造成的，而不是你的设计。",
    frameConversionAlternative:
      "要查看原始画框，请改为上传 .fig 文件。",
    frameImportedStatus:
      "已作为画框的 Studio 重建结果保存到库 — 继续构建前请先与你的 Figma 文件对比。",
  },
  "zh-TW": {
    openCanvas: "開啟設計畫布",
    openCanvasWithSource: "你匯入的設計已在那裡開啟。",
    openCanvasEmpty: "匯入的 .fig 會在設計畫布中讀取，你也可以在那裡選擇檔案。",
    frameNeedsProjectChip: "需要開啟一個專案",
    frameNeedsProjectNotice:
      "Figma 框架會存入專案的設計紀錄，因此讀取前必須先開啟一個專案。在此之前不會向 Figma 送出任何內容。",
    frameNeedsProjectStatus:
      "請先開啟一個專案 —— Figma 框架會存入該專案的設計紀錄。",
    frameProducesChip: "用 Cogo 元件重建",
    fileProducesChip: "你畫的原框架",
    frameConversionNotice:
      "Studio 會從 Figma 讀取這個框架的圖層，並用 Cogo 元件重建它。原始美術稿本身不會被帶過來，所以你得到的是 Studio 的重建結果 — 與 Figma 檔案看起來不同的地方，是轉換造成的，而不是你的設計。",
    frameConversionAlternative:
      "要查看原始框架，請改為上傳 .fig 檔案。",
    frameImportedStatus:
      "已作為框架的 Studio 重建結果儲存到素材庫 — 繼續建置前請先與你的 Figma 檔案對照。",
  },
};

export function getSourceDoorsCopy(locale: LocaleCode): SourceDoorsCopy {
  return COPY[locale] ?? COPY.en;
}

/**
 * Whether importing a Figma FRAME is currently refused for want of a project.
 *
 * Same field, same falsy test, as the `import_figma_frame` handler's guard.
 */
export function figmaFrameNeedsOpenProject(
  currentProjectId: string | null | undefined,
): boolean {
  return !currentProjectId;
}
